import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { UserPlus, UserMinus, Loader2 } from "lucide-react";

const FollowButton = ({ userId, token, onFollowChange }) => {
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    checkFollowStatus();
  }, [userId, token]);

  const checkFollowStatus = async () => {
    if (!token || !userId) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/users/${userId}/is-following`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsFollowing(response.data.is_following);
    } catch (err) {
      console.error("Error checking follow status:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async () => {
    if (!token) {
      toast.error("Please log in to follow users");
      return;
    }
    
    setActionLoading(true);
    try {
      if (isFollowing) {
        await axios.delete(`${API}/users/${userId}/follow`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFollowing(false);
        toast.success("Unfollowed user");
      } else {
        await axios.post(`${API}/users/${userId}/follow`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFollowing(true);
        toast.success("Now following user");
      }
      
      if (onFollowChange) {
        onFollowChange(!isFollowing);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update follow status");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Button variant="outline" size="sm" disabled className="border-white/20">
        <Loader2 className="w-4 h-4 animate-spin" />
      </Button>
    );
  }

  return (
    <Button
      variant={isFollowing ? "outline" : "default"}
      size="sm"
      onClick={handleFollow}
      disabled={actionLoading}
      className={isFollowing 
        ? "border-white/20 text-[#A1A1AA] hover:border-red-500/50 hover:text-red-400 hover:bg-red-500/10" 
        : "btn-gold"
      }
      data-testid="follow-btn"
    >
      {actionLoading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : isFollowing ? (
        <>
          <UserMinus className="w-4 h-4 mr-2" />
          Unfollow
        </>
      ) : (
        <>
          <UserPlus className="w-4 h-4 mr-2" />
          Follow
        </>
      )}
    </Button>
  );
};

export default FollowButton;
